import React from 'react';
import { sortBy, groupBy, pick } from 'lodash';
import { format as formatDate } from 'date-fns';
import { toast } from 'react-toastify';
import { Button } from 'reactstrap';
import exifr from 'exifr';

import firebase from '../firebase';
import { resizeImage } from '../util';
import TenantLink from './TenantLink';
import EditButton from './EditButton';
import ModelFormModal from './modals/ModelFormModal';
import { fields as lectureFrameImagesFields, expiresOnOf, isAutoDeletionEnabled } from '../shared/models/lectureFrameImages';

export default function LectureImagesManager({ event, lecture, lectureFrames, lectureFrameImagesDoc, lectureFrameImagesRef, lectureId, onStartSendingImages, onStopSendingImages }) {
  const [dragOverFrameIndex, setDragOverFrameIndex] = React.useState(null);
  const [selectedImageUrls, setSelectedImageUrls] = React.useState([]);
  const [isSelectingDestination, setIsSelectingDestination] = React.useState(false);
  const expiresOn = expiresOnOf(lectureFrameImagesDoc, lecture);
  const protectedImagesCount = (lectureFrameImagesDoc?.images || []).filter(_ => _.isProtected).length;

  const handleBulkUpload = async (files) => {
    if (files.length === 0) return;

    const validateFiles = Array.from(files).every(_ => _.type.match(/^image/));
    if (!validateFiles) {
      toast.error('画像以外が選択されています。');
      return;
    }

    try {
      toast.info(`${files.length}枚の画像を処理中...`);

      const existingImages = lectureFrameImagesDoc?.images || [];

      const results = await Promise.all(
        Array.from(files).map(async (file) => {
          try {
            const isDuplicate = existingImages.some(
              img => img.name === file.name && img.size === file.size
            );
            if (isDuplicate) {
              return { file, error: '重複ファイル' };
            }

            const exifData = await exifr.parse(file);
            const photoDate = exifData?.DateTimeOriginal || exifData?.DateTime || exifData?.CreateDate || new Date(file.lastModified);

            if (!photoDate) {
              return { file, error: '日時情報なし' };
            }

            const matchedFrameIndex = lectureFrames.findIndex((frame) => {
              const dateStr = formatDate(lecture.date.toDate(), 'yyyy-MM-dd');
              const startTimeStr = formatDate(frame.startAt.toDate(), 'HH:mm:ss');
              const endTimeStr = formatDate(frame.endAt.toDate(), 'HH:mm:ss');

              const startDateTime = new Date(`${dateStr} ${startTimeStr}`).getTime();
              const endDateTime = new Date(`${dateStr} ${endTimeStr}`).getTime();
              const photoTime = new Date(photoDate).getTime();

              return photoTime >= startDateTime && photoTime <= endDateTime;
            });

            if (matchedFrameIndex === -1) {
              return { file, photoDate, frameIndex: -1 };
            }

            return { file, photoDate, frameIndex: matchedFrameIndex };
          } catch (e) {
            console.error('日時解析エラー:', e);
            return { file, error: '日時解析失敗' };
          }
        })
      );

      const successFiles = results.filter(_ => _.frameIndex !== undefined);
      const errorFiles = results.filter(_ => _.error);
      const duplicateFiles = errorFiles.filter(_ => _.error === '重複ファイル');
      const outsideFrameFiles = successFiles.filter(_ => _.frameIndex === -1);

      if (duplicateFiles.length > 0) {
        toast.info(`${duplicateFiles.length}枚の重複ファイルをスキップしました`);
      }

      if (outsideFrameFiles.length > 0) {
        toast.info(`${outsideFrameFiles.length}枚の画像を「枠外」として登録します`);
      }

      if (errorFiles.length > duplicateFiles.length) {
        const otherErrors = errorFiles.length - duplicateFiles.length;
        console.warn('処理できなかったファイル:', errorFiles);
        toast.warning(`${otherErrors}枚の画像に日時情報がありません`);
      }

      if (successFiles.length === 0) {
        toast.error('アップロード可能な画像がありませんでした');
        return;
      }

      const groupedByFrame = groupBy(successFiles, 'frameIndex');

      await Promise.all(
        Object.entries(groupedByFrame).map(async ([frameIndex, fileInfos]) => {
          const storagePath = `lectures/${lectureId}/frames/${frameIndex}/images`;

          await Promise.all(
            fileInfos.map(async ({ file, photoDate }) => {
              const timestamp = new Date().getTime();
              const path = `${storagePath}/${timestamp}_${file.name}`;
              const storageRef = firebase.storage().ref();
              const fileRef = storageRef.child(path);
              const uploadFile = await resizeImage(file, 2048);
              await fileRef.put(uploadFile, { contentType: file.type });
              const downloadUrl = await fileRef.getDownloadURL();

              const resizedFile = await resizeImage(file, 800);
              const thumbPath = `${storagePath}/thumb_800_${timestamp}_${file.name}`;
              const thumbRef = storageRef.child(thumbPath);
              await thumbRef.put(resizedFile, { contentType: file.type });
              const thumbnailUrl = await thumbRef.getDownloadURL();

              await lectureFrameImagesRef.set(
                {
                  images: firebase.firestore.FieldValue.arrayUnion({
                    frameIndex: parseInt(frameIndex),
                    uploadedAt: new Date(),
                    photoDate: new Date(photoDate),
                    downloadUrl,
                    thumbnailUrl,
                    storagePath: path,
                    thumbPath,
                    ...pick(file, ['name', 'type', 'size']),
                  }),
                },
                { merge: true }
              );
            })
          );
        })
      );

      toast.success(`${successFiles.length}枚の画像をアップロードしました`);
    } catch (e) {
      console.error(e);
      toast.error('アップロードに失敗しました');
    }
  };

  const handleDragStart = (e, image) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/json', JSON.stringify(image));
  };

  const handleDragOver = (e, frameIndex) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverFrameIndex(frameIndex);
  };

  const handleDragLeave = (e) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragOverFrameIndex(null);
  };

  const handleDrop = async (e, targetFrameIndex) => {
    e.preventDefault();
    setDragOverFrameIndex(null);

    try {
      const image = JSON.parse(e.dataTransfer.getData('application/json'));

      if (image.frameIndex === targetFrameIndex) return;

      const updatedImages = (lectureFrameImagesDoc?.images || []).map(_ =>
        _.downloadUrl === image.downloadUrl ? { ..._, frameIndex: targetFrameIndex } : _
      );

      await lectureFrameImagesRef.set({ images: updatedImages }, { merge: true });
      toast.success('画像を移動しました');
    } catch (e) {
      console.error(e);
      toast.error('移動に失敗しました');
    }
  };

  const handleBulkMove = async (targetFrameIndex) => {
    try {
      const updatedImages = (lectureFrameImagesDoc?.images || []).map(_ =>
        selectedImageUrls.includes(_.downloadUrl) ? { ..._, frameIndex: targetFrameIndex } : _
      );

      await lectureFrameImagesRef.set({ images: updatedImages }, { merge: true });
      toast.success(`${selectedImageUrls.length}枚の画像を移動しました`);
      setSelectedImageUrls([]);
      setIsSelectingDestination(false);
    } catch (e) {
      console.error(e);
      toast.error('移動に失敗しました');
    }
  };

  const updateProtection = async (imageUrls, isProtected) => {
    try {
      const updatedImages = (lectureFrameImagesDoc?.images || []).map(_ =>
        imageUrls.includes(_.downloadUrl) ? { ..._, isProtected } : _
      );

      await lectureFrameImagesRef.set({ images: updatedImages }, { merge: true });
      toast.success(`${imageUrls.length}枚の画像を${isProtected ? '保護しました' : '保護解除しました'}`);
    } catch (e) {
      console.error(e);
      toast.error('失敗しました');
    }
  };

  const handleBulkProtect = async (isProtected) => {
    await updateProtection(selectedImageUrls, isProtected);
    setSelectedImageUrls([]);
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`選択した${selectedImageUrls.length}枚の画像を削除しますか？`)) {
      return;
    }

    try {
      const imagesToDelete = (lectureFrameImagesDoc?.images || []).filter(_ =>
        selectedImageUrls.includes(_.downloadUrl)
      );

      const storageRef = firebase.storage().ref();

      await Promise.all(
        imagesToDelete.map(async (image) => {
          if (image.storagePath) {
            const fileRef = storageRef.child(image.storagePath);
            await fileRef.delete().catch(err => {
              console.error('オリジナル画像の削除に失敗:', err);
            });
          }

          if (image.thumbPath) {
            const thumbRef = storageRef.child(image.thumbPath);
            await thumbRef.delete().catch(err => {
              console.error('サムネイル画像の削除に失敗:', err);
            });
          }
        })
      );

      const updatedImages = (lectureFrameImagesDoc?.images || []).filter(_ =>
        !selectedImageUrls.includes(_.downloadUrl)
      );

      await lectureFrameImagesRef.set({ images: updatedImages }, { merge: true });
      toast.success(`${selectedImageUrls.length}枚の画像を削除しました`);
      setSelectedImageUrls([]);
    } catch (e) {
      console.error(e);
      toast.error('削除に失敗しました');
    }
  };

  return (
    <div>
      <div className="mb-3 d-flex align-items-center gap-2">
        <Button>
          <label className="m-0 cursor-pointer">
            <span className="fas fa-upload mr-2" />
            画像をアップロード
            <input
              type="file"
              className="d-none"
              multiple
              accept="image/*"
              onChange={_ => handleBulkUpload(_.target.files)}
            />
          </label>
        </Button>
        {lecture?.imagesSendingStarted ? (
          <>
            <div className="alert alert-success mb-0 py-2 px-3 d-flex align-items-center">
              <span className="fas fa-check-circle mr-2" />
              送信開始済み
            </div>
            <Button color="warning" onClick={onStopSendingImages}>
              <span className="fas fa-stop mr-2" />
              送信を停止
            </Button>
          </>
        ) : (
          <Button onClick={onStartSendingImages}>
            <span className="fas fa-paper-plane mr-2" />
            画像送信を開始
          </Button>
        )}
        {isAutoDeletionEnabled && <div className="ml-auto d-flex align-items-center gap-2">
          {lectureFrameImagesDoc?.imagesDeletedAt && (
            <span className="text-muted small">
              {formatDate(lectureFrameImagesDoc.imagesDeletedAt.toDate(), 'yyyy/MM/dd')}に{lectureFrameImagesDoc.deletedImagesCount}枚を自動削除済み
            </span>
          )}
          {expiresOn && (
            <span className="badge badge-warning p-2">
              <span className="fas fa-clock mr-1" />
              {formatDate(expiresOn, 'yyyy/MM/dd')}に自動削除予定（保護{protectedImagesCount}枚を除く）
            </span>
          )}
          <EditButton
            type="set"
            label="自動削除設定"
            itemRef={lectureFrameImagesRef}
            FormModal={ModelFormModal}
            formProps={{ title: '自動削除設定', fields: lectureFrameImagesFields }}
          />
        </div>}
      </div>
      <div style={{ maxHeight: 'calc(100vh - 270px)', overflowY: 'auto' }}>
        {[...lectureFrames, null].map((frame, i) => {
          const frameIndex = frame ? i : -1;
          const images = sortBy(
            (lectureFrameImagesDoc?.images || []).filter(_ => _.frameIndex === frameIndex),
            _ => _.photoDate.toDate()
          );

          if (!frame && images.length === 0) return null;

          const title = frame
            ? `${formatDate(frame.startAt.toDate(), 'HH:mm')} 〜 ${formatDate(frame.endAt.toDate(), 'HH:mm')}`
            : '枠外';

          const isDragOver = dragOverFrameIndex === frameIndex;
          const imageUrls = images.map(_ => _.downloadUrl);
          const allSelected = imageUrls.length > 0 && imageUrls.every(_ => selectedImageUrls.includes(_));
          const someSelected = imageUrls.some(_ => selectedImageUrls.includes(_)) && !allSelected;

          const toggleAllSelection = () => {
            if (allSelected) {
              setSelectedImageUrls(_ => _.filter(url => !imageUrls.includes(url)));
            } else {
              setSelectedImageUrls(_ => [...new Set([..._, ...imageUrls])]);
            }
          };

          return (
            <div
              key={frameIndex}
              className={`mb-4 p-3 rounded ${isDragOver ? 'border border-info' : 'bg-light border border-transparent'}`}
              style={{
                transition: 'all 0.2s ease',
                ...(isDragOver && { backgroundColor: '#e3f2fd' })
              }}
              onDragOver={e => handleDragOver(e, frameIndex)}
              onDragLeave={handleDragLeave}
              onDrop={e => handleDrop(e, frameIndex)}
            >
              <h5 className="mb-3 d-flex align-items-center">
                {images.length > 0 && (
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={input => {
                      if (input) input.indeterminate = someSelected;
                    }}
                    onChange={toggleAllSelection}
                    className="mr-2"
                    style={{ width: 18, height: 18, cursor: 'pointer' }}
                  />
                )}
                {title}
                {images.length > 0 && <span className="badge badge-info ml-2">{images.length}枚</span>}
                {frame && (
                  <Button
                    size="sm"
                    color="primary"
                    className="ml-2"
                    tag={TenantLink}
                    to={`/events/${event.id}/lectureImages?lectureId=${lectureId}&frameIndex=${frameIndex}`}
                    target="_blank"
                  >
                    <span className="fas fa-images mr-1" />
                    画像閲覧URL
                  </Button>
                )}
              </h5>
              {images.length === 0 ? (
                <div className="text-muted p-3 border border-dashed" style={{ minHeight: 100 }}>
                  画像がありません
                </div>
              ) : (
                <div className="d-flex flex-wrap gap-2">
                  {images.map((image) => {
                    const { downloadUrl, thumbnailUrl } = image;
                    const isSelected = selectedImageUrls.includes(downloadUrl);

                    const onClickDelete = async () => {
                      if (!window.confirm('本当に削除しますか？')) return;

                      try {
                        const storageRef = firebase.storage().ref();

                        if (image.storagePath) {
                          const fileRef = storageRef.child(image.storagePath);
                          await fileRef.delete().catch(err => {
                            console.error('オリジナル画像の削除に失敗:', err);
                          });
                        }

                        if (image.thumbPath) {
                          const thumbRef = storageRef.child(image.thumbPath);
                          await thumbRef.delete().catch(err => {
                            console.error('サムネイル画像の削除に失敗:', err);
                          });
                        }

                        await lectureFrameImagesRef.set(
                          {
                            images: (lectureFrameImagesDoc?.images || []).filter(_ => _ !== image),
                          },
                          { merge: true }
                        );

                        toast.success('画像を削除しました');
                      } catch (e) {
                        console.error('削除エラー:', e);
                        toast.error('画像の削除に失敗しました');
                      }
                    };

                    const toggleSelection = () => {
                      setSelectedImageUrls(_ =>
                        _.includes(downloadUrl)
                          ? _.filter(url => url !== downloadUrl)
                          : [..._, downloadUrl]
                      );
                    };

                    return (
                      <div
                        key={downloadUrl}
                        className={`card p-3 d-flex flex-column align-items-center position-relative ${isSelected ? 'border-primary' : ''}`}
                        style={{ width: 300, cursor: 'move', borderWidth: isSelected ? 3 : 1 }}
                        draggable
                        onDragStart={e => handleDragStart(e, image)}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={toggleSelection}
                          className="position-absolute"
                          style={{ top: 8, left: 8, width: 20, height: 20, cursor: 'pointer' }}
                          onClick={e => e.stopPropagation()}
                        />
                        <Button
                          className="position-absolute p-0"
                          style={{ top: 8, right: 8, width: 28, height: 28 }}
                          color="danger"
                          size="sm"
                          onClick={onClickDelete}
                        >
                          <span className="fas fa-times" />
                        </Button>
                        <Button
                          className="position-absolute p-0"
                          style={{ top: 8, right: 44, width: 28, height: 28 }}
                          color={image.isProtected ? 'success' : 'light'}
                          size="sm"
                          title={image.isProtected ? '自動削除から保護中（クリックで解除）' : '自動削除から保護する'}
                          onClick={() => updateProtection([downloadUrl], !image.isProtected)}
                        >
                          <span className="fas fa-shield-alt" />
                        </Button>
                        <a href={downloadUrl} target="_blank" rel="noopener noreferrer">
                          <img src={thumbnailUrl || downloadUrl} className="img-fluid" style={{ maxHeight: 200, cursor: 'pointer' }} />
                        </a>
                        <div className="text-muted small mt-2">
                          {formatDate(image.photoDate.toDate(), 'yyyy/MM/dd HH:mm:ss')}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {selectedImageUrls.length > 0 && !isSelectingDestination && (
        <div
          className="d-flex align-items-center justify-content-center gap-3 p-3 bg-white border-top shadow-lg"
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1000
          }}
        >
          <span className="font-weight-bold">
            {selectedImageUrls.length}枚選択中
          </span>
          <Button
            color="primary"
            onClick={() => setIsSelectingDestination(true)}
          >
            <span className="fas fa-arrow-right mr-2" />
            移動
          </Button>
          <Button
            color="success"
            onClick={() => handleBulkProtect(true)}
          >
            <span className="fas fa-shield-alt mr-2" />
            保護
          </Button>
          <Button
            color="secondary"
            onClick={() => handleBulkProtect(false)}
          >
            保護解除
          </Button>
          <Button
            color="danger"
            onClick={handleBulkDelete}
          >
            <span className="fas fa-trash mr-2" />
            削除
          </Button>
          <Button
            color="secondary"
            onClick={() => setSelectedImageUrls([])}
          >
            選択解除
          </Button>
        </div>
      )}
      {isSelectingDestination && (
        <div
          className="d-flex flex-column align-items-center justify-content-center p-4 bg-white border-top shadow-lg"
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            maxHeight: '50vh',
            overflowY: 'auto'
          }}
        >
          <h5 className="mb-3">移動先の時間枠を選択してください</h5>
          <div className="d-flex flex-wrap gap-2 justify-content-center mb-3">
            {[...lectureFrames, null].map((frame, i) => {
              const frameIndex = frame ? i : -1;
              const title = frame
                ? `${formatDate(frame.startAt.toDate(), 'HH:mm')} 〜 ${formatDate(frame.endAt.toDate(), 'HH:mm')}`
                : '枠外';

              return (
                <Button
                  key={frameIndex}
                  color="info"
                  onClick={() => {
                    handleBulkMove(frameIndex);
                  }}
                  style={{ minWidth: 150 }}
                >
                  {title}
                </Button>
              );
            })}
          </div>
          <Button
            color="secondary"
            onClick={() => setIsSelectingDestination(false)}
          >
            キャンセル
          </Button>
        </div>
      )}
    </div>
  );
}
